/**
 * ReviewPopup — one change shown in the review window, for the surfaces that
 * propose a single change outside the review queue: a chat edit of highlighted
 * text, a drafted section, an agent's request to write.
 *
 * It is the same view as the queue's review window (``ReviewWorkspace``):
 * Show changes / Before and after / New text, Approve, Reject and Revise. It
 * opens over the task pane; Expand moves it into a window over Word
 * (``review.html``), Minimise brings it back. The owner keeps the decision:
 * every click goes to its callbacks.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { surfaceColors } from "../tokens";
import { parseMessage, type ReviewItem, type ReviewState, type WindowToParent } from "./reviewProtocol";
import { ReviewWorkspace } from "./ReviewWorkspace";
import { dialogsSupported, reviewWindowUrl, WINDOW_SIZE } from "./useReviewWindow";

export interface ReviewPopupProps {
  item: ReviewItem;
  /** ``applying`` while an approve is being written, ``revising`` while a
   *  revision is drafted. */
  phase: "reviewing" | "applying" | "revising";
  progressLabel?: string | null;
  error?: string | null;
  /** ``content``: the writer's edit of the proposed text (only with ``canEdit``). */
  onApprove: (content?: string) => void;
  /** Offer "Edit text" on a text change; the edit is passed to ``onApprove``. */
  canEdit?: boolean;
  onReject: () => void;
  /** Absent when this change cannot be revised. */
  onRevise?: (guidance: string) => void;
  /** The writer closed the pop-up without deciding (the change stays pending). */
  onClose: () => void;
}

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    backgroundColor: surfaceColors.paper,
  },
});

export const ReviewPopup: React.FC<ReviewPopupProps> = ({
  item,
  phase,
  progressLabel = null,
  error = null,
  onApprove,
  onReject,
  onRevise,
  onClose,
  canEdit = false,
}) => {
  const styles = useStyles();
  const [mode, setMode] = useState<"overlay" | "dialog">("overlay");
  const dialogRef = useRef<Office.Dialog | null>(null);
  const readyRef = useRef(false);

  const state: ReviewState = useMemo(
    () => ({
      items: [item],
      activeId: item.id,
      phase,
      error,
      progressLabel,
      approvingAll: false,
      canRevise: Boolean(onRevise),
      canEdit,
    }),
    [item, phase, error, progressLabel, onRevise, canEdit],
  );
  const latest = useRef({ state, onApprove, onReject, onRevise, onClose });
  latest.current = { state, onApprove, onReject, onRevise, onClose };

  const closeDialog = useCallback(() => {
    try {
      dialogRef.current?.close();
    } catch {
      // already closed
    }
    dialogRef.current = null;
    readyRef.current = false;
  }, []);

  const send = useCallback((s: ReviewState) => {
    if (!dialogRef.current || !readyRef.current) return;
    try {
      dialogRef.current.messageChild(JSON.stringify({ type: "state", state: s }));
    } catch {
      // The window closed under us; the close event tidies up.
    }
  }, []);
  useEffect(() => {
    if (mode === "dialog") send(state);
  }, [mode, state, send]);

  const expand = useCallback(() => {
    if (dialogRef.current) return;
    Office.context.ui.displayDialogAsync(reviewWindowUrl(), { ...WINDOW_SIZE, displayInIframe: true }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) return; // stay over the pane
      const dialog = result.value;
      dialogRef.current = dialog;
      readyRef.current = false;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        if (!("message" in arg)) return;
        const msg = parseMessage<WindowToParent>(arg.message);
        const h = latest.current;
        if (!msg) return;
        if (msg.type === "ready") {
          readyRef.current = true;
          send(h.state);
        } else if (msg.type === "approve") h.onApprove(msg.content);
        else if (msg.type === "reject") h.onReject();
        else if (msg.type === "revise") h.onRevise?.(msg.guidance);
        else if (msg.type === "minimise") {
          closeDialog();
          setMode("overlay");
        } else if (msg.type === "close") {
          closeDialog();
          h.onClose();
        }
      });
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        // Closed with the window's own X: the change stays pending.
        dialogRef.current = null;
        readyRef.current = false;
        latest.current.onClose();
      });
      setMode("dialog");
    });
  }, [closeDialog, send]);

  // Unmounted (decided, or the owner dropped the change): close the window.
  useEffect(() => closeDialog, [closeDialog]);

  if (mode === "dialog") return null;
  const view = (
    // biome-ignore lint/a11y/useSemanticElements: a full-pane overlay, not a native modal
    <div className={styles.overlay} role="dialog" aria-label="Review change">
      <ReviewWorkspace
        state={state}
        onSelect={() => {}}
        onApprove={(_id, content) => onApprove(content)}
        onReject={() => onReject()}
        onRevise={(_id, guidance) => onRevise?.(guidance)}
        onApproveAll={() => {}}
        onStopAll={() => {}}
        onClose={onClose}
        placement="pane"
        onResize={dialogsSupported() ? expand : undefined}
      />
    </div>
  );
  return typeof document !== "undefined" ? createPortal(view, document.body) : view;
};

export default ReviewPopup;
